/**
 * Renders full HTML report in a hidden iframe, rasterizes with html2canvas, splits across A4 pages in jsPDF.
 */
export async function downloadHtmlAsPdf(html: string, fileName: string): Promise<void> {
  const [{ default: html2canvas }, { default: JsPDF }] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.title = "pdf-render";
  iframe.style.cssText =
    "position:fixed;left:-9999px;top:0;width:820px;height:1px;visibility:hidden;border:0;pointer-events:none";

  document.body.appendChild(iframe);

  try {
    const idoc = iframe.contentDocument;
    if (!idoc) throw new Error("iframe document missing");

    idoc.open();
    idoc.write(html);
    idoc.close();

    await new Promise<void>((resolve, reject) => {
      const finish = () => requestAnimationFrame(() => resolve());
      if (idoc.readyState === "complete") {
        finish();
        return;
      }
      iframe.onload = () => finish();
      iframe.onerror = () => reject(new Error("iframe load failed"));
    });

    await new Promise((r) => setTimeout(r, 150));

    const body = idoc.body;
    const canvas = await html2canvas(body, {
      scale: 1.25,
      useCORS: true,
      logging: false,
      backgroundColor: "#14151a",
      windowWidth: 820,
      scrollX: 0,
      scrollY: 0,
    });

    const imgData = canvas.toDataURL("image/png", 0.92);
    const pdf = new JsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW;
    const imgH = (canvas.height * imgW) / canvas.width;

    let heightLeft = imgH;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgW, imgH, undefined, "FAST");
    heightLeft -= pageH;

    while (heightLeft > 0) {
      position = heightLeft - imgH;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgW, imgH, undefined, "FAST");
      heightLeft -= pageH;
    }

    pdf.save(fileName);
  } finally {
    iframe.remove();
  }
}
